import { useEffect, useMemo } from 'react';
import JsonLd from '../../components/feature/JsonLd';

const AboutPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    document.title = 'About Call Center Pros – AI BPO Nearshore Solutions Since 2012 | Boca Raton, FL';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Learn how Call Center Pros has been delivering AI-powered BPO nearshore solutions since 2012. Our team of experts drives innovation in call center outsourcing from Boca Raton, FL.');
  }, []);

  const aboutSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Call Center Pros',
    url: `${siteUrl}/about-us`,
    description: 'Learn how Call Center Pros has been delivering AI-powered BPO nearshore solutions since 2012.',
    mainEntity: {
      '@type': 'Organization',
      name: 'Call Center Pros',
      foundingDate: '2012',
      numberOfEmployees: { '@type': 'QuantitativeValue', value: 500 },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '4400 N Federal Hwy, Suite 401',
        addressLocality: 'Boca Raton',
        addressRegion: 'FL',
        postalCode: '33431',
        addressCountry: 'US',
      },
      description: 'Leading AI-powered BPO nearshore outsourcing solutions provider since 2012, combining artificial intelligence and expert customer service teams.',
    },
  }), [siteUrl]);

  return (
    <div className="min-h-screen bg-white">
      <JsonLd id="about-page-schema" data={aboutSchema} />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-700"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              About <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Call Center Pros</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Leading BPO nearshore solutions provider with over a decade of experience disrupting the customer service landscape
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-block px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold mb-6">
                  Our Story
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Transforming BPO Since 2012</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Call Center Pros first nearshore location was opened in 2012. This experience has positioned us to become the leading provider offering BPO outsourcing solutions in the near-shore region.
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Our innovative solutions improve our client's operations with greater efficiency, improved technology, streamlined processes and the ability to scale and retain huge growth immediately!
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We combine AI and ML to understand your current gaps and implement significant changes to increase efficiency while improving NPS and lowering your overhead.
                </p>
                <div className="grid grid-cols-3 gap-6 mt-10">
                  <div>
                    <div className="text-3xl font-bold text-blue-600 mb-1">500+</div>
                    <div className="text-sm text-gray-600">Projects Completed</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-blue-600 mb-1">98%</div>
                    <div className="text-sm text-gray-600">Client Satisfaction</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-blue-600 mb-1">12+</div>
                    <div className="text-sm text-gray-600">Years Experience</div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-2xl transform rotate-3"></div>
                <img 
                  src="https://public.readdy.ai/ai/img_res/9f1a24e0cb3c5fdd8cc701d66a036405.jpg" 
                  alt="Call Center Pros Team" 
                  className="relative w-full h-auto rounded-2xl shadow-2xl object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold mb-4">
                Our Values
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What Drives Us Forward</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Our core values shape every decision we make and every solution we deliver
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-target-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Excellence</h3>
                <p className="text-gray-600 leading-relaxed">Delivering exceptional BPO solutions that exceed client expectations through innovation and dedication.</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-team-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Partnership</h3>
                <p className="text-gray-600 leading-relaxed">Building long-term partnerships with our clients by understanding their unique business needs.</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-lightbulb-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Innovation</h3>
                <p className="text-gray-600 leading-relaxed">Leveraging cutting-edge technology and AI to transform traditional BPO processes.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold mb-4">
                Leadership
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Our experienced leadership team brings decades of combined expertise in BPO operations and customer service excellence
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="group">
                <div className="relative overflow-hidden rounded-2xl mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20confident%20male%20business%20executive%20CEO%20in%20modern%20office%20wearing%20business%20casual%20attire%20corporate%20headshot%20photography%20clean%20background%20natural%20lighting&width=400&height=400&seq=leader-ceo-001&orientation=squarish" 
                    alt="Jared Schagrin - CEO" 
                    className="w-full h-80 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Jared Schagrin</h3>
                <p className="text-blue-600 font-semibold mb-3">Chief Executive Officer</p>
                <p className="text-gray-600 text-sm leading-relaxed">Leading innovation in AI-powered BPO solutions with over 15 years of industry experience.</p>
              </div>
              <div className="group">
                <div className="relative overflow-hidden rounded-2xl mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20confident%20female%20business%20executive%20COO%20in%20modern%20office%20wearing%20business%20attire%20corporate%20headshot%20photography%20clean%20background%20natural%20lighting&width=400&height=400&seq=leader-coo-002&orientation=squarish" 
                    alt="Sarah Johnson - COO" 
                    className="w-full h-80 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Sarah Johnson</h3>
                <p className="text-blue-600 font-semibold mb-3">Chief Operations Officer</p>
                <p className="text-gray-600 text-sm leading-relaxed">Ensuring operational excellence and scalability across all nearshore locations.</p>
              </div>
              <div className="group">
                <div className="relative overflow-hidden rounded-2xl mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20confident%20male%20technology%20executive%20CTO%20in%20modern%20office%20wearing%20business%20casual%20attire%20corporate%20headshot%20photography%20clean%20background%20natural%20lighting&width=400&height=400&seq=leader-cto-003&orientation=squarish" 
                    alt="Michael Rodriguez - CTO" 
                    className="w-full h-80 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Michael Rodriguez</h3>
                <p className="text-blue-600 font-semibold mb-3">Chief Technology Officer</p>
                <p className="text-gray-600 text-sm leading-relaxed">Driving technological innovation and AI integration in customer service solutions.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Partner with Us?</h2>
          <p className="text-blue-100 mb-10 max-w-2xl mx-auto text-lg">
            Let's discuss how our BPO outsourcing solutions can transform your business operations
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact-us" 
              className="inline-block bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get Free Consultation
            </a>
            <a 
              href="/services" 
              className="inline-block bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Explore Services
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;